import * as React from "react";
import styled from "@emotion/styled";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import Modal from "@/components/molecules/Modal";
import Button from "@/components/atoms/Button";
import theme from "@/theme";
import breakpoint from "@/utils/breakpoint";
import { isBlank } from "@/utils/is";
import MiniTitle from "@/components/atoms/MiniTitle";
import InputText from "@/components/atoms/InputText";
import MediaQueryContext, { initMediaQueryStore } from "@/contexts/MediaQuery";
import withEffect, { InjectProps } from "./effect";
import searchIcon from "@/assets/icons/search.svg";
import Search from "@/assets/icons/Search";
import { hexToRgbA } from "@/utils/color";
import { useTheme } from "@/hooks/useTheme";

type SearchMansionProps = Record<string, any> & React.HTMLAttributes<HTMLDivElement>;

const ModalHeader = styled(Modal.Header)`
  border-bottom: 2px solid #2a7593;
  padding: 0 0 8px 0;
  margin-bottom: 25px;
  ${breakpoint.lessThan("sm")`
  margin-bottom: 10px;
  `}

  &.override {
    padding: 0 0 0 0;
    border: none;
  }
`;

const ModalBody = styled(Modal.Body)`
  border: none;
  overflow: scroll;

  &.override {
    border: none;
    ::-webkit-scrollbar {
      width: 4px;
    }
    ::-webkit-scrollbar-track {
      background: #ffffff;
    }
    ::-webkit-scrollbar-thumb {
      background: #cccccc;
    }
    ::-webkit-scrollbar-thumb:hover {
      background: #555;
    }
  }
`;

const ModalFooter = styled(Modal.Footer)`
  margin-top: 20px;
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
`;

const TextWrapper = styled.div`
  font-size: 20px;
  font-weight: bold;
  color: #524939;
  padding: 10px 5px;

  ${breakpoint.lessThan("sm")`
    font-size: 14px;
`}
`;

const Count = styled.span<{ color?: string }>`
  color: ${({ color = "#fa7c1d" }) => color};
  font-size: 26px;
  font-weight: bold;
`;

const StyledCount = styled.span<{ color?: string }>`
  color: ${({ color = "#fa7c1d" }) => color};
  font-size: 24px;
  font-weight: 700;
  padding-left: 7px;
`;

const CountText = styled.span<{ color?: string }>`
  color: ${({ color = "#fa7c1d" }) => color};
  font-size: 16px;
  font-weight: 700;
`;

const BodyWrapper = styled.div``;

const FooterWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  padding: 10px;

  ${breakpoint.lessThan("sm")`
    justify-content: center;
  `}
`;

const SearchButton = styled(Button)<{
  secondaryColor?: string;
}>`
  background: #dd690a;
  width: 100px;
  height: 45px;
  padding: 2px 5px;
  font-size: 22px;
  font-weight: bold;
  margin: 0 0 0 3px;

  ${breakpoint.lessThan("sm")`
  width: 90px;
  padding: 6px 10px
  font-size: 12px;
`}

  &:disabled {
    background: #d5d5d5;
    color: ##9f9f9f;
  }

  &.override {
    background: ${({ secondaryColor = "#43A0DF" }) => secondaryColor} url(${searchIcon}) 0% 0% no-repeat padding-box;
    opacity: 1;
    width: 40px;
    height: 40px;
    margin-left: 10px;
    background-position: center;
  }
`;

const CloseButton = styled(Button)`
  background: #dd690a;
  width: 100px;
  height: 45px;
  padding: 2px 5px;
  font-size: 22px;
  font-weight: bold;
  margin: 3px 5px 0;

  ${breakpoint.lessThan("sm")`
    width: 90%;
    padding: 5 10px;
  `}
`;

const CloseCenterButton = styled(Button)`
  width: 170px;
  height: 44px;
  background: #ffffff;
  border: 1px solid #333333;
  border-radius: 24px;
  opacity: 1;
  font-size: 14px;
  font-weight: 700;
  color: #333333;
  margin: auto;
  ${breakpoint.lessThan("sm")`
    padding: 5 10px;
  `}
`;

const MansionTable = styled.table`
  background: #fff;
  width: 100%;
  &.override {
    border-collapse: separate;
    border-spacing: 0 15px;
  }
`;

const List: React.FC = ({ children }) => {
  const { isNewTheme } = useTheme();
  return (
    <MansionTable
      className={classNames({
        override: isNewTheme,
      })}
    >
      <tbody>{children}</tbody>
    </MansionTable>
  );
};

const ListItem = styled.tr<{ secondaryColor?: string }>`
  border: 1px solid #038ec8;
  cursor: pointer;
  opacity: 1;
  transition: all 0.3s;

  &:hover {
    background: #eee;
    opacity: 0.8;
  }

  &.listItemOverride {
    border: 1px solid #f7f7f7;
    cursor: pointer;
    opacity: 1;
    color: #333333;
    transition: all 0.3s;
    background: #f7f7f7;
    font-size: 16px;
    font-weight: 700;
    &:hover {
      color: #333333;
      background: ${({ secondaryColor = "#e6f4fa" }) => secondaryColor};
      opacity: 0.8;
    }
  }
`;

const ListItemText = styled.td`
  text-align: left;
  text-decoration: none;
  font-weight: bold;
  font-size: 17px;
  color: #524939;
  padding: 10px;
  &.listItemOverride {
    border-radius: 5px;
    color: #333333 !important;
    font-size: 16px !important;
    font-weight: 700 !important;
    ${breakpoint.lessThan("sm")`
    border-radius: 5px;
   `}
  }
`;

const ListItemArrow = styled.td`
  padding-right: 10px;
`;

const TextBox = styled.div`
  display: inline-flex;
  flex-wrap: no-wrap;
  align-items: center;
  ${breakpoint.lessThan("sm")`
    width: 100%;
    margin: 0 0 10px;
   `}
`;

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 45px;
  height: 45px;
  border: 2px solid #959595;
  border-right: none !important;
  &.override {
    height: 40px;
    width: 40px;
    border: 1px solid #cccccc;
    border-radius: 5px 0px 0px 5px;
  }
`;

const FilterText = styled(InputText)`
  font-size: 17px;
  font-weight: bold;
  color: #524939;
  align-self: center;
  margin: 0 10px 0 0;
  width: 300px;
  ::placeholder {
    color: #cccccc;
    padding-left: 10px;
  }
  &.filterOverride {
    height: 40px;
    color: #333333;
    border-left: none !important;
    border-radius: 0px 5px 5px 0px;
    border: 1px solid #cccccc;
    font-size: 14px !important;
    font-weight: 400 !important;
  }
  ${breakpoint.lessThan("sm")`
  width: calc(100% - 45px);
`}
`;
const Text = styled.div`
  font-size: 16px;
  font-weight: 700;
`;

const SubText = styled.div`
  font-size: 14px;
  font-weight: 400;
`;

const SearchMansion = ({
  propertyName,
  searchMansions,
  open,
  closeModal,
  mansions,
  handleSelectMansion,
}: SearchMansionProps & InjectProps) => {
  const { COLOR_THEME, isNewTheme } = useTheme();
  const { current: range } = React.useContext(MediaQueryContext) || initMediaQueryStore("md");

  const [fillText, setFillText] = React.useState("");

  const handleChangeFilter = (event: React.ChangeEvent<HTMLInputElement>) => setFillText(event.target.value);

  const handleClose = () => {
    setFillText("");
    closeModal();
  };

  const handleSearchMansion = () => {
    if (isBlank(propertyName)) {
      return;
    }
    searchMansions(propertyName);
  };

  const handleClickMansion = (mansionId: string) => () => {
    handleSelectMansion(mansionId);
  };

  const options: Option[] = React.useMemo(() => {
    return mansions.map(mansion => ({
      label: `${mansion.name}(${mansion.address.pref}${mansion.address.city}${mansion.address.town})`,
      value: `${mansion.id}`,
    }));
  }, [mansions]);

  const renderSearchBtn = () => {
    if (isNewTheme) {
      return (
        <SearchButton
          disabled={isBlank(propertyName)}
          onClick={handleSearchMansion}
          secondaryColor={COLOR_THEME.extras?.secondary}
          className={classNames({
            override: isNewTheme,
          })}
        />
      );
    } else {
      return (
        <SearchButton disabled={isBlank(propertyName)} onClick={handleSearchMansion}>
          検索
        </SearchButton>
      );
    }
  };

  const numberWithCommas = (x: number) => {
    return ` ${x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  };
  const renderMiniTitle = () => {
    if (isNewTheme) {
      return (
        <MiniTitle color="#333333" fontSize="14px" fontWeight="500">
          {"検索結果 : "}
          <StyledCount color={COLOR_THEME.extras?.primary}>
            {options ? numberWithCommas(options.filter(option => option.label.includes(fillText)).length) : 0}
          </StyledCount>
          <CountText color={COLOR_THEME.extras?.primary}>{"件"}</CountText>
        </MiniTitle>
      );
    } else {
      return (
        <MiniTitle>
          {"マンション検索結果: "}
          <Count>{options ? options.filter(option => option.label.includes(fillText)).length : 0}</Count>
          {" 件"}
        </MiniTitle>
      );
    }
  };

  const modalStyle = {
    overflow: "hidden",
  };

  const cssOverrideModalStyle = () => {
    const cssOverrideModalStyle = {
      overflow: "hidden",
      padding: "30px 60px",
      margin: "auto",
      borderRadius: "10px",
    };
    if (isNewTheme) {
      cssOverrideModalStyle.margin = "1% 15.8%";
      if (range === "sm") {
        cssOverrideModalStyle.padding = "10px 20px 20px 20px";
        cssOverrideModalStyle.margin = "10% 15px";
      }
    }

    return cssOverrideModalStyle;
  };

  const getInclosedLabel = (label: string) => {
    const openingIndex = label.indexOf("(");
    const closingIndex = label.indexOf(")");
    return { text: label.slice(0, openingIndex).trim(), subText: label.slice(openingIndex + 1, closingIndex).trim() };
  };

  const renderMansionText = (label: string) => {
    if (isNewTheme) {
      if (range === "sm") {
        const mobileText = getInclosedLabel(label);
        return (
          <ListItemText
            className={classNames({
              listItemOverride: isNewTheme,
            })}
            color={COLOR_THEME.extras?.secondary}
          >
            <Text> {mobileText.text}</Text>
            <SubText>{mobileText.subText}</SubText>
          </ListItemText>
        );
      }
    }
    return (
      <ListItemText
        className={classNames({
          listItemOverride: isNewTheme,
        })}
        color={COLOR_THEME.extras?.secondary}
      >
        {label}
      </ListItemText>
    );
  };

  return (
    <>
      {renderSearchBtn()}
      {open && (
        <Modal
          top="30px"
          open={open}
          width={range === "sm" ? "100vw" : "80vw"}
          height={isNewTheme ? "85vh" : "70vh"}
          style={isNewTheme ? cssOverrideModalStyle() : modalStyle}
          flex
        >
          <ModalHeader
            className={classNames({
              override: isNewTheme,
            })}
          >
            <HeaderWrapper>
              <TextWrapper>{renderMiniTitle()}</TextWrapper>
              <TextBox>
                <IconWrapper
                  className={classNames({
                    override: isNewTheme,
                  })}
                >
                  {isNewTheme ? (
                    <Search height="17.05" width="17.05" fillColor="#333333" />
                  ) : (
                    <FontAwesomeIcon icon={faSearch} />
                  )}
                </IconWrapper>
                <FilterText
                  value={fillText}
                  placeholder={isNewTheme ? "マンションの絞り込み" : "絞り込み"}
                  onChange={handleChangeFilter}
                  aria-label={isNewTheme ? "マンションの絞り込み" : "絞り込み"}
                  className={classNames({
                    filterOverride: isNewTheme,
                  })}
                />
              </TextBox>
            </HeaderWrapper>
          </ModalHeader>
          <ModalBody
            className={classNames({
              override: isNewTheme,
            })}
          >
            <BodyWrapper>
              {options &&
                (() => {
                  const filterMansions = options.filter(option => option.label.includes(fillText));
                  if (filterMansions.length > 0) {
                    return (
                      <List>
                        {filterMansions.map(mansion => (
                          <ListItem
                            className={classNames({
                              listItemOverride: isNewTheme,
                            })}
                            secondaryColor={
                              COLOR_THEME.extras?.secondary
                                ? (hexToRgbA(COLOR_THEME.extras?.secondary as string, 0.3) as string)
                                : "#e6f4fa"
                            }
                            key={`${mansion.value}`}
                            onClick={handleClickMansion(mansion.value)}
                          >
                            {renderMansionText(mansion.label)}
                            {!isNewTheme && (
                              <ListItemArrow>
                                <FontAwesomeIcon icon={faChevronRight} />
                              </ListItemArrow>
                            )}
                          </ListItem>
                        ))}
                      </List>
                    );
                  }
                  return (
                    <TextWrapper>
                      <MiniTitle
                        fontSize={isNewTheme ? "14px" : "17px"}
                        fontWeight={400}
                        color={isNewTheme ? "#333333" : theme.main.warning}
                      >
                        該当するマンションが見つかりませんでした。
                        <br />
                        条件を変更して再検索、もしくは「条件を指定して査定する」をご利用ください
                      </MiniTitle>
                    </TextWrapper>
                  );
                })()}
            </BodyWrapper>
          </ModalBody>
          <ModalFooter>
            <FooterWrapper>
              {isNewTheme ? (
                <CloseCenterButton onClick={handleClose} color="danger">
                  閉じる
                </CloseCenterButton>
              ) : (
                <CloseButton onClick={handleClose} color="danger">
                  閉じる
                </CloseButton>
              )}
            </FooterWrapper>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default withEffect(SearchMansion);
